import { useState } from 'react';
import { Code, GitBranch } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Mock data
const mockErrorTimeline = [
  { time: '00:00', errors: 2 },
  { time: '00:05', errors: 1 },
  { time: '00:10', errors: 4 },
  { time: '00:15', errors: 0 },
  { time: '00:20', errors: 3 }
];

const mockRecentErrors = [
  {
    id: '1',
    timestamp: new Date(Date.now() - 300000),
    service: 'vapi',
    type: 'ValidationError',
    message: 'Invalid phone number format in request payload',
    count: 12,
    stackTrace: 'Error: Invalid phone number format...'
  },
  {
    id: '2',
    timestamp: new Date(Date.now() - 600000),
    service: 'openrouter',
    type: 'RateLimitError',
    message: 'Rate limit exceeded for model gpt-4',
    count: 8,
    stackTrace: 'RateLimitError: Rate limit exceeded...'
  },
  {
    id: '3',
    timestamp: new Date(Date.now() - 900000),
    service: 'n8n',
    type: 'ConnectionError',
    message: 'Failed to connect to webhook endpoint',
    count: 3,
    stackTrace: 'ConnectionError: ECONNREFUSED...'
  }
];

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  trend?: number;
  status?: 'success' | 'warning' | 'error';
  onClick?: () => void;
}

function MetricCard({ title, value, subtitle, trend, status, onClick }: MetricCardProps) {
  const statusStyles = {
    success: 'border-green-200 bg-green-50',
    warning: 'border-yellow-200 bg-yellow-50',
    error: 'border-red-200 bg-red-50'
  };

  return (
    <Card 
      className={`cursor-pointer hover:shadow-sm transition-shadow ${status ? statusStyles[status] : ''}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="text-2xl font-bold">{value}</div>
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
        {trend !== undefined && (
          <div className={`text-xs font-medium ${trend > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface ErrorTimelineProps {
  data: Array<{ time: string; errors: number }>;
  onPointClick?: (point: any) => void;
}

function ErrorTimeline({ data, onPointClick }: ErrorTimelineProps) {
  return (
    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis 
          dataKey="time" 
          tick={{ fontSize: 10 }}
          stroke="#6b7280"
        />
        <YAxis 
          tick={{ fontSize: 10 }}
          stroke="#6b7280"
        />
        <Tooltip 
          contentStyle={{ 
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }}
        />
        <Line
          type="monotone"
          dataKey="errors"
          stroke="#ef4444"
          strokeWidth={2}
          dot={{ fill: '#ef4444', strokeWidth: 2, r: 4 }}
          onClick={onPointClick}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

interface ErrorTableProps {
  errors: any[];
  columns: Array<{
    key: string;
    label: string;
    format?: string;
    truncate?: number;
    render?: (error: any) => React.ReactNode;
  }>;
}

function ErrorTable({ errors, columns }: ErrorTableProps) {
  const formatValue = (value: any, format?: string, truncate?: number) => {
    if (format === 'relative') {
      const now = new Date();
      const diff = now.getTime() - value.getTime();
      const minutes = Math.floor(diff / 60000);
      return minutes < 60 ? `${minutes}m ago` : `${Math.floor(minutes / 60)}h ago`;
    }
    
    if (truncate && typeof value === 'string' && value.length > truncate) {
      return value.substring(0, truncate) + '...';
    }
    
    return value;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            {columns.map((col) => (
              <th key={col.key} className="text-left p-2 font-medium">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {errors.map((error) => (
            <tr key={error.id} className="border-b hover:bg-gray-50">
              {columns.map((col) => (
                <td key={col.key} className="p-2">
                  {col.render 
                    ? col.render(error)
                    : formatValue(error[col.key], col.format, col.truncate)
                  }
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ErrorAnalysis() {
  const [searchTerm, setSearchTerm] = useState('');

  const showErrorDetails = (errors: any[]) => {
    console.log('Show error details:', errors);
  };

  const showStackTrace = (error: any) => {
    console.log('Show stack trace:', error);
  };

  const createIssue = (error: any) => {
    console.log('Create issue:', error);
  };

  const filterByTimeRange = (range: string) => {
    console.log('Filter by time range:', range);
  };

  const exportErrors = () => {
    console.log('Export errors to CSV');
  };

  const errors = {
    total: 12,
    trend: -15,
    rate: 0.02,
    unique: 8,
    affectedUsers: 3,
    timeline: mockErrorTimeline,
    recent: mockRecentErrors.filter(error => 
      error.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      error.service.toLowerCase().includes(searchTerm.toLowerCase())
    )
  };

  return (
    <div className="space-y-4">
      {/* Error Summary */}
      <div className="grid grid-cols-4 gap-4">
        <MetricCard
          title="Total Errors"
          value={errors.total}
          subtitle="Last 24h"
          trend={errors.trend}
          onClick={() => filterByTimeRange('24h')}
        />
        <MetricCard
          title="Error Rate"
          value={`${errors.rate}%`}
          subtitle="Of total requests"
          status={errors.rate > 1 ? 'error' : 'success'}
        />
        <MetricCard
          title="Unique Errors"
          value={errors.unique}
          subtitle="Different error types"
        />
        <MetricCard
          title="Affected Users"
          value={errors.affectedUsers}
          subtitle="Experienced errors"
        />
      </div>

      {/* Error Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Error Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <ErrorTimeline
            data={errors.timeline}
            onPointClick={(point) => showErrorDetails(point.errors)}
          />
        </CardContent>
      </Card>

      {/* Error Table with Stack Traces */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Recent Errors</CardTitle>
            <div className="flex gap-2">
              <Input
                placeholder="Filter errors..."
                className="w-48 h-8 text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button size="sm" variant="outline" onClick={exportErrors}>
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ErrorTable
            errors={errors.recent}
            columns={[
              { key: 'timestamp', label: 'Time', format: 'relative' },
              { key: 'service', label: 'Service' },
              { key: 'type', label: 'Error Type' },
              { key: 'message', label: 'Message', truncate: 50 },
              { key: 'count', label: 'Count' },
              { 
                key: 'actions', 
                label: '', 
                render: (error) => (
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => showStackTrace(error)}
                    >
                      <Code className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => createIssue(error)}
                    >
                      <GitBranch className="w-3 h-3" />
                    </Button>
                  </div>
                )
              }
            ]}
          />
        </CardContent>
      </Card>
    </div>
  );
}